import * as React from "react";

type Props = {
  className: string;
  play: boolean | null | undefined;
  src: string;
  onLoad?: () => void;
  showErrorComponent: () => void;
  fallbackSrc: string | null | undefined;
  limitReplays: boolean;
};

type State = {
  retryCount: number;
  playCount: number;
};

const RETRY_LIMIT = 5;
const RETRIES_BEFORE_FALLBACK = 2;
const GIF_REPLAYS = 3;

export default class VideoComponent extends React.PureComponent<Props, State> {
  static defaultProps = {
    play: true
  };

  state = {
    retryCount: 0,
    playCount: 0
  };

  video = React.createRef<HTMLVideoElement>();
  playPausePromise: Promise<any> | undefined;

  componentDidMount() {
    if (this.video.current) {
      if (this.props.limitReplays) {
        this.video.current.addEventListener("ended", this.onEnded);
      }
    }

    if (this.props.play) {
      this.playVideo();
    }
  }

  componentWillUnmount() {
    if (this.video.current) {
      this.video.current.removeEventListener("ended", this.onEnded);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const videoElement = this.video && this.video.current;
    if (!videoElement) return;
    if (this.props.limitReplays && this.state.playCount >= GIF_REPLAYS) return;

    if (this.props.play && !prevProps.play) {
      this.playVideo();
    } else if (!this.props.play && prevProps.play) {
      this.pauseVideo();
    }
  }

  onEnded = () => {
    if (this.props.limitReplays) {
      this.setState(
        (prevState: State) => ({
          playCount: prevState.playCount + 1
        }),
        () => {
          if (this.state.playCount < GIF_REPLAYS) {
            this.playVideo();
          }
        }
      );
    }
  };

  async playVideo() {
    const videoElement = this.video && this.video.current;

    try {
      await this.playPausePromise;
      this.playPausePromise = videoElement!
        .play()
        .catch(err => console.error("Unable to play video: ", err));
    } catch (err: any) {
      console.error("Unable to play video: ", err);
    }
  }

  async pauseVideo() {
    const videoElement = this.video && this.video.current;

    await this.playPausePromise;
    this.playPausePromise = videoElement && (videoElement.pause() as any);
  }

  handleError = (e: Error) => {
    if (this.state.retryCount < RETRY_LIMIT - 1) {
      this.setState((prevState: State) => ({
        retryCount: prevState.retryCount + 1
      }));
    } else {
      this.props.showErrorComponent();
    }
  };

  render() {
    const { className, src, fallbackSrc, onLoad, limitReplays } = this.props;
    const { retryCount } = this.state;
    return (
      <video
        key={`video-${this.props.src}-retry-${retryCount}`}
        className={className}
        loop={!limitReplays}
        muted
        playsInline
        onLoadedData={onLoad}
        ref={this.video}
      >
        <source
          type="video/mp4"
          onCanPlay={onLoad}
          src={retryCount < RETRIES_BEFORE_FALLBACK ? src : fallbackSrc || src}
          onError={this.handleError as any}
        />
      </video>
    );
  }
}
